import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import min from 'lodash/min';

import ChartComponent from './ChartComponent';
import ButtonGroup from './ButtonGroup';

import translateKey from '../../charts/translations';
import { dummyServer } from '../../../../config';

const moment = require('moment');

const periods = [
  'month',
  'day',
  'hour',
];

const parseDate = (date, altMode = false, period = '') => {
  const year = date.slice(0, 4);
  const month = String(date.slice(4, 6)); // I love Date format so much!
  const day = date.slice(6, 8) || '01';
  const hour = date.slice(9, 11) || '00';

  if (!altMode) {
    return moment(`${year}-${month}-${day}T${hour}:00:00`).toDate();
  }
  switch (period) {
    case 'month':
      return `${year}-${month}`;
    case 'day':
      return `${year}-${month}-${day}`;
    case 'hour':
      return `${year}-${month}-${day} ${hour}:00`;
    default:
      return '';
  }
};

const updateEventDataDates = (data) => {
  const SUBTRACT_MONTHS = 3;
  const customMonthsAgo = moment().subtract(SUBTRACT_MONTHS, 'months');
  const updatedData = {};

  Object.keys(data).forEach(period => {
    updatedData[period] = {};

    Object.keys(data[period]).forEach((dateKey, index) => {
      let newKey;

      if (dateKey.includes('_')) {
        const [datePart, hourPart] = dateKey.split('_');
        const newDateMoment = moment(customMonthsAgo).add(index, 'days'); // adjust day
        newKey = `${newDateMoment.format('YYYYMMDD')}_${hourPart}`;
      } else if (dateKey.length === 6) {
        newKey = moment(customMonthsAgo).add(index, 'months').format('YYYYMM'); // adjust month
      } else {
        newKey = moment(customMonthsAgo).add(index, 'days').format('YYYYMMDD'); // adjust day
      }

      updatedData[period][newKey] = data[period][dateKey];
    });
  });

  return updatedData;
};

class ConnectionsChart extends Component {
  dimensions = [
    { name: 'showPortal', color: '#9B59B6' },
    { name: 'logins', color: '#818E92' },
    { name: 'uniqueLogins', color: '#FFD000' },
    { name: 'videoFinish', color: '#4CD137' },
    // { name: 'appFinish', color: '#FF3301' },
    { name: 'formFinish', color: ' #2F6BF4' },
  ]

  constructor(props) {
    super(props);
    this.state = {
      updated: false,
      period: 'day',
      data: dummyServer ? updateEventDataDates(props.data) : props.data, // update dates to the last 3 months
    };
  }

  // A hack to fix bug with broken chart when you change datazoom and periods.
  componentDidUpdate() {
    const { updated } = this.state;
    // This is fine, see
    // https://github.com/airbnb/javascript/issues/1875 and
    // https://reactjs.org/docs/react-component.html#componentdidupdate
    /* eslint-disable react/no-did-update-set-state */
    if (updated) {
      this.setState({
        updated: false,
      });
    }
  }

  buildSeries = (data) => this.dimensions.map(d => ({
    type: 'line',
    name: d.name,
    encode: {
      x: 'date', y: d.name,
    },
    areaStyle: {
      color: d.color,
      opacity: 0.24,
    },
    lineStyle: {
      color: d.color,
      width: 2,
    },
    itemStyle: {
      color: d.color,
    },
    symbol: 'none',
    smooth: true,
    sampling: 'average',
    markPoint: data.length === 1
      ? {
        symbol: 'circle',
        symbolSize: value => Math.max(10 * Math.log10(value), 15),
        data: [{
          name: 'maximum',
          type: 'max'
        }]
      }
      : null
  }))

  buildTarget = (data, period) => {
    const firstDate = min(Object.keys(data));
    if (!firstDate) return {};
    let timeKey;
    let format;
    switch (period) {
      case 'month':
        timeKey = 'M';
        format = 'YYYYMM';
        break;
      case 'day':
        timeKey = 'd';
        format = 'YYYYMMDD';
        break;
      case 'hour':
        timeKey = 'h';
        format = 'YYYYMMDD_HH';
    }
    const start = moment(parseDate(firstDate));
    const end = moment();
    const result = {};
    for (const m = start; m.isSameOrBefore(end); m.add(1, timeKey)) {
      const key = m.format(format);
      result[key] = data[key] || {};
    }
    return result;
  }

  transformData = (target, period) => Object.keys(target).map(key => ({
    date: parseDate(key, true, period),
    dateAsValue: parseDate(key),
    showPortal: target[key].showPortal || 0,
    logins: target[key].logins || 0,
    uniqueLogins: target[key].uniqueLogins || 0,
    videoFinish: target[key].videoFinish || 0,
    appFinish: target[key].appFinish || 0,
    formFinish: target[key].formFinish || 0,
  })).sort((v1, v2) => v1.dateAsValue - v2.dateAsValue)

  parse = (data, period) => {
    const target = this.buildTarget(data[period], period);
    return this.transformData(target, period);
  }

  onPeriodChange = index => {
    this.setState({
      period: periods[index],
      updated: true,
    });
  }

  render() {
    const { period, updated, data } = this.state;

    const graphData = updated ? [] : this.parse(data, period);

    return (
      <React.Fragment>
        <ButtonGroup
          names={periods.map(p => this.props.t(translateKey(p)))}
          selected={[period === 'month', period === 'day', period === 'hour']}
          title={this.props.t('ANALYTICS.BUTTON_GROUP_TITLE')}
          onClick={this.onPeriodChange}
        />
        <ChartComponent
          data={graphData}
          series={this.buildSeries(graphData)}
          period={period}
        />
      </React.Fragment>
    );
  }
}

ConnectionsChart.propTypes = {
  data: PropTypes.shape({
    day: PropTypes.object,
    hour: PropTypes.object,
    month: PropTypes.object,
  }).isRequired,
};

export default withTranslation(['translation'])(ConnectionsChart);
